import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useDeliveries,
  useDiscrepancies,
  useStartReceiving,
  ReportDiscrepancyModal,
} from '@/features/receiving';
import type {
  DeliveryStatus, Discrepancy, Delivery 
} from '@/features/receiving';
import { toast } from 'sonner';
import {
  DEFAULT_SITE_ID, TABS, type TabId 
} from './receiving-constants';
import { DeliveriesView } from './deliveries-view';
import { DiscrepanciesView } from './discrepancies-view';

export function ReceivingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<DeliveryStatus | undefined>(undefined);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState<Discrepancy | null>(null);
  const [reportingDelivery, setReportingDelivery] = useState<Delivery | null>(null);

  const activeTab = useMemo<TabId>(() => {
    if (location.pathname.includes('/goods')) return 'goods';
    if (location.pathname.includes('/discrepancies')) return 'discrepancies';
    return 'expected';
  }, [location.pathname]);

  const effectiveStatus = useMemo(() => {
    if (activeTab === 'expected') return 'scheduled' as const;
    if (activeTab === 'goods') return 'in-progress' as const;
    return selectedStatus;
  }, [activeTab, selectedStatus]);

  const {
    data, isLoading, error, refetch, isFetching 
  } = useDeliveries(DEFAULT_SITE_ID, { status: effectiveStatus });
  const { data: allData } = useDeliveries(DEFAULT_SITE_ID);
  const {
    data: discrepanciesData, isLoading: discrepanciesLoading 
  } = useDiscrepancies(DEFAULT_SITE_ID);

  const startReceivingMutation = useStartReceiving();

  const deliveries = data?.data ?? [];
  const discrepancies = discrepanciesData?.data ?? [];
  const pendingDiscrepancies = discrepancies.filter(
    (d) => d.status === 'pending_supplier_response' || d.status === 'pending_receiver_confirmation'
  );

  const stats = useMemo(() => {
    const dels = allData?.data ?? [];
    return {
      total: dels.length,
      scheduled: dels.filter((d) => d.status === 'scheduled').length,
      inProgress: dels.filter((d) => d.status === 'in-progress').length,
      completed: dels.filter((d) => d.status === 'completed').length,
      partial: dels.filter((d) => d.status === 'partial').length,
    };
  }, [allData]);

  const handleStartReceiving = (deliveryId: string) => {
    const delivery = deliveries.find((d) => d.deliveryId === deliveryId);
    startReceivingMutation.mutate(deliveryId, {
      onSuccess: () => {
        toast.success('Receiving started');
        setSelectedDelivery(null);
        if (delivery?.poNumber) {
          navigate(`/receiving/goods/${delivery.poNumber}`);
        }
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to start receiving');
      },
    });
  };

  const handleMarkComplete = (deliveryId: string) => {
    toast.success(`Delivery ${deliveryId} marked as complete`);
    setSelectedDelivery(null);
    refetch();
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Updated delivery records');
      refetch();
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Receiving</h1>
          <p className="text-slate-500">Manage incoming deliveries and quality checks</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} className="border-slate-200">
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <TabNavigation
        activeTab={activeTab}
        pendingCount={pendingDiscrepancies.length}
        onNavigate={navigate}
      />

      {activeTab === 'discrepancies' ? (
        <DiscrepanciesView
          discrepancies={discrepancies}
          isLoading={discrepanciesLoading}
          selectedDiscrepancy={selectedDiscrepancy}
          onSelectDiscrepancy={setSelectedDiscrepancy}
        />
      ) : (
        <DeliveriesView
          activeTab={activeTab}
          deliveries={deliveries}
          isLoading={isLoading}
          error={error}
          stats={stats}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          selectedDelivery={selectedDelivery}
          onSelectDelivery={setSelectedDelivery}
          onBulkUpload={handleBulkUpload}
          onRefetch={refetch}
          onReportDiscrepancy={setReportingDelivery}
          onStartReceiving={handleStartReceiving}
          onMarkComplete={handleMarkComplete}
        />
      )}

      {reportingDelivery && (
        <ReportDiscrepancyModal delivery={reportingDelivery} onClose={() => setReportingDelivery(null)} />
      )}
    </div>
  );
}

interface TabNavigationProps {
  readonly activeTab: TabId;
  readonly pendingCount: number;
  readonly onNavigate: (href: string) => void;
}

function TabNavigation({
  activeTab, pendingCount, onNavigate 
}: TabNavigationProps) {
  return (
    <div className="border-b border-slate-200">
      <nav className="flex gap-4">
        {TABS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          const showBadge = tab.id === 'discrepancies' && pendingCount > 0;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onNavigate(tab.href)}
              className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              <TabIcon className="h-4 w-4" />
              {tab.label}
              {showBadge && (
                <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs font-medium">
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
